import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useValidate, useValidators } from "formvalidation/hooks";
import { useSemanticUiInputRef } from "utilities/hookutils";
import { Utils } from "common/utils";
import { ButtonPrimary, ButtonText } from "./Atoms/Button";
import OptionalTextInput from "./ValidatedFields/OptionalTextInput";
import { PhosphorTrashButton } from "./Atoms/PhosphorIconButton";
import ValidatorContext from "formvalidation/FormContext";

interface BookkeepingEmailListProps {
  bookkeepingId: number;
  value: string[];
  onChange: () => void;
}

export const BookkeepingEmailList = (props: BookkeepingEmailListProps) => {
  const [isAdding, setIsAdding] = useState(false);

  const handleAddClick = () => {
    setIsAdding(true);
  };

  const handleAddSuccess = () => {
    setIsAdding(false);
    props.onChange();
  };

  const handleAddCancel = () => {
    setIsAdding(false);
  };
  const validators = useValidators();

  return (
    <ValidatorContext settings={validators.validatorSettings}>
      <div className="flex-column gap">
        <EmailListItems
          bookkeepingId={props.bookkeepingId}
          emails={props.value}
          onChange={props.onChange}
        />
        <div>
          {isAdding ? (
            <AddEmailInput
              bookkeepingId={props.bookkeepingId}
              onSuccess={handleAddSuccess}
              onCancel={handleAddCancel}
              validators={validators}
            />
          ) : (
            <ButtonText
              label={RESX.Buttons.Add}
              onClick={handleAddClick}
              style={{ paddingLeft: 0, paddingRight: 0 }}
            />
          )}
        </div>
      </div>
    </ValidatorContext>
  );
};

interface EmailListItemsProps {
  bookkeepingId: number;
  emails: string[];
  onChange: () => void;
}

const EmailListItems = (props: EmailListItemsProps) => {
  if (props.emails.length === 0) {
    return null;
  }

  return (<div className="flex-column gap-larger">
    {props.emails.map((email) => (
      <EmailListItem
        key={email}
        bookkeepingId={props.bookkeepingId}
        email={email}
        onChange={props.onChange}
      />
    ))}
  </div>);
};

interface EmailListItemProps {
  bookkeepingId: number;
  email: string;
  onChange: () => void;
}

const EmailListItem = (props: EmailListItemProps) => {
  const removeMutation = useMutation({
    mutationFn: async () => {
      return await window.CoreApi.Bookkeeping.RemoveEmail({
        bookkeepingId: props.bookkeepingId,
        email: props.email
      });
    },
    onSuccess: () => {
      props.onChange();
    },
    retry: false
  });

  const handleRemove = async () => {
    if (removeMutation.isLoading || removeMutation.isSuccess) {
      return;
    }
    await removeMutation.mutateAsync();
  };

  const isDisabled = removeMutation.isLoading || removeMutation.isSuccess;

  return (
    <div className="flex gap">
      <div className="grow1">
        {props.email}
      </div>

      <PhosphorTrashButton layout="none" onClick={handleRemove} disabled={isDisabled} />
    </div >
  );
};

interface AddEmailInputProps {
  bookkeepingId: number;
  onSuccess: () => void;
  onCancel: () => void;
  validators: ReturnType<typeof useValidators>;
}

const AddEmailInput = (props: AddEmailInputProps) => {
  const [emailValue, setEmailValue] = useState("");
  const semanticRef = useSemanticUiInputRef();

  const validateEmail = async (input: string): Promise<boolean> => {
    return Utils.isValidEmailAddress(input);
  };

  const validation = useValidate({
    key: `bookkeeping-add-email-${props.bookkeepingId}`,
    title: "Email Address",
    value: emailValue,
    validate: validateEmail,
    ref: semanticRef.ref
  });

  const addMutation = useMutation({
    mutationFn: () => window.CoreApi.Bookkeeping.AddEmail({
      bookkeepingId: props.bookkeepingId,
      email: emailValue
    }),
    onSuccess: () => props.onSuccess(),
    retry: false
  });

  const handleSave = async () => {
    if (addMutation.isLoading || addMutation.isSuccess) {
      return;
    }

    if (emailValue.trim().length === 0) {
      props.onCancel();
      return;
    }

    const isValid = await props.validators.validateAll();
    if (isValid.hasErrors) {
      isValid.scrollToFirstError();
    } else {
      await addMutation.mutateAsync();
    }
  };

  const isDisabled = addMutation.isLoading || addMutation.isSuccess;

  return (
    <div className="flex gap" >
      <div className="grow1">
        <OptionalTextInput
          maxLength={serverReference.constants.mailAddress.maxEmailLength}
          innerRef={semanticRef}
          value={emailValue}
          onChange={value => setEmailValue(value)}
          hasError={validation.error.hasError}
          onEnter={handleSave}
          onEsc={props.onCancel}
          disabled={isDisabled}
          focusOnMount
        />
      </div>
      <ButtonPrimary
        label="Save"
        onClick={handleSave}
        disabled={isDisabled}
        loading={addMutation.isLoading}
      />
    </div>
  );
};

